import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Detail from '../../../types/detail/Detail';
import DetailWriteDTO from '../../../types/detail/DetailWriteDTO';
import Page from '../../../types/shared/Page';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class DetailService {

    // The resource name matches the backend controller's mapping.
    private RESOURCE: string = 'sample-category-detail';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches details from the backend.
     *
     * @param search The search query to filter details by.
     * @param page The page number to fetch.
     * @param size The number of elements per page.
     * @return A paginated list of details.
     */
    public async getDetails(search?: string, page: number = 0, size: number = 10): Promise<Page<Detail>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Detail>>(this.RESOURCE, params);
    }

    /**
     * Fetches all details from the backend.
     *
     * @return A list of all details.
     */
    public async getAllDetails(): Promise<Detail[]> {
        return await this.baseService.get<Detail[]>(`${ this.RESOURCE }/all`);
    }

    /**
     * Fetches details that are not in a specific category.
     */
    public async getDetailsNotInCategory(categoryId: number): Promise<Detail[]> {
        return await this.baseService.get<Detail[]>(`${ this.RESOURCE }/not-in-category/${ categoryId }`);
    }

    /**
     * Fetches a specific detail by its ID.
     *
     * @param id The ID of the detail.
     * @return The detail.
     */
    public async getDetail(id: number): Promise<Detail> {
        return await this.baseService.get<Detail>(`${ this.RESOURCE }/${ id }`);
    }

    /**
     * Creates a new detail in the backend.
     *
     * @param detail The detail data to create.
     */
    public async createDetail(detail: DetailWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, detail);
    }

    /**
     * Updates an existing detail in the backend.
     *
     * @param detail The detail data to update.
     */
    public async updateDetail(detail: DetailWriteDTO): Promise<void> {
        return await this.baseService.put<void>(`${ this.RESOURCE }/${ detail.id }`, detail);
    }

    /**
     * Fetches details by category from the backend.
     *
     * @param categoryId The ID of the category to fetch details for.
     * @return The details.
     */
    public async getDetailsByCategory(categoryId: number): Promise<Detail[]> {
        return await this.baseService.get<Detail[]>(`${ this.RESOURCE }/category/${ categoryId }`);
    }

    /**
     * Sets the categories for a detail.
     *
     * @param detailId The ID of the detail to set categories for.
     * @param categoryIds The IDs of the categories to set.
     */
    public async setCategoriesForDetail(detailId: number, categoryIds: number[]): Promise<void> {
        return await this.baseService.put<void>(`${ this.RESOURCE }/${ detailId }/categories`, categoryIds);
    }

    /**
     * Removes a category from a detail.
     *
     * @param detailId The ID of the detail.
     * @param categoryId The ID of the category to remove.
     * @return A promise that resolves when the category has been removed.
     */
    public async removeCategoryFromDetail(detailId: number, categoryId: number): Promise<void> {
        return await this.baseService.delete<void>(`${ this.RESOURCE }/${ detailId }/categories/${ categoryId }`);
    }

    /**
     * Deletes a detail from the backend.
     *
     * @param id The ID of the detail to delete.
     */
    public async deleteDetail(id: number): Promise<void> {
        return await this.baseService.delete<void>(`${ this.RESOURCE }/${ id }`);
    }
}
